import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, ArrowLeft, X } from "lucide-react";

// Lightweight, dependency-free coach-mark tour. Spotlights a sequence of
// elements (found by data-testid), navigating between routes as needed.
export function GuidedTour({ steps, storageKey, run }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  // Auto-start once (when `run` is true and not already seen).
  useEffect(() => {
    if (run && !localStorage.getItem(storageKey)) {
      setIdx(0);
      setActive(true);
    }
  }, [run, storageKey]);

  const finish = useCallback(() => {
    localStorage.setItem(storageKey, "1");
    setActive(false);
    setRect(null);
  }, [storageKey]);

  // Locate the current step's target: navigate if needed, then poll for it.
  useEffect(() => {
    if (!active) return;
    const step = steps[idx];
    if (!step) return;
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
    let tries = 0;
    setRect(null);
    const timer = setInterval(() => {
      tries += 1;
      const el = document.querySelector(`[data-testid="${step.testid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        clearInterval(timer);
      } else if (tries > 25) {
        setRect(null); // fall back to centred card
        clearInterval(timer);
      }
    }, 120);
    return () => clearInterval(timer);
  }, [active, idx, location.pathname, navigate, steps]);

  if (!active) return null;
  const step = steps[idx];
  const last = idx === steps.length - 1;

  const pad = 8;
  const spot =
    rect && {
      position: "fixed",
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
      borderRadius: 12,
      boxShadow: "0 0 0 9999px rgba(15,23,42,0.62)",
      border: "2px solid rgba(129,140,248,0.9)",
      pointerEvents: "none",
      zIndex: 100,
      transition: "all 0.2s ease",
    };

  // Card position: under the target if there's room, else above; else centred.
  let cardStyle = {
    position: "fixed",
    zIndex: 101,
    width: 340,
    maxWidth: "calc(100vw - 24px)",
  };
  if (rect) {
    const below = rect.top + rect.height + 320 < window.innerHeight;
    cardStyle.top = below ? rect.top + rect.height + pad + 10 : Math.max(12, rect.top - 12 - 210);
    cardStyle.left = Math.min(
      Math.max(12, rect.left),
      window.innerWidth - 340 - 12
    );
  } else {
    cardStyle.top = "50%";
    cardStyle.left = "50%";
    cardStyle.transform = "translate(-50%, -50%)";
  }

  return (
    <div data-testid="guided-tour" className="pointer-events-none">
      {rect ? (
        <div style={spot} data-testid="tour-spotlight" />
      ) : (
        <div className="fixed inset-0 z-[100] bg-slate-900/60" />
      )}
      <div style={cardStyle} className="pointer-events-auto rounded-xl border border-border bg-card p-5 shadow-2xl">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-indigo-600">
            <Sparkles className="h-4 w-4" />
            <span className="label-caps">Demo tour</span>
          </div>
          <button
            type="button"
            onClick={finish}
            data-testid="tour-skip-btn"
            className="rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            aria-label="Skip tour"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <h3 className="font-display text-lg font-bold" data-testid="tour-title">{step.title}</h3>
        <p className="mt-1.5 text-sm text-muted-foreground" data-testid="tour-body">{step.body}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">{idx + 1} / {steps.length}</span>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <Button size="sm" variant="ghost" className="gap-1" onClick={() => setIdx((i) => i - 1)} data-testid="tour-back-btn">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </Button>
            )}
            {last ? (
              <Button size="sm" onClick={finish} data-testid="tour-finish-btn">Got it</Button>
            ) : (
              <Button size="sm" className="gap-1" onClick={() => setIdx((i) => i + 1)} data-testid="tour-next-btn">
                Next <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
